import { useContext, useEffect, useState } from 'react'
import { RoomContext, RoomContextType } from '../RoomContext'
import { UserContext, UserContextType } from '../UserContext'
import { sleep } from '../utils'
import useRoomActions from './useRoomActions'

const state = {
    busy: false,
    recording: false,
    subscribers: new Set<() => void>(),
    setRecording(val: boolean) {
        if (val !== this.recording) {
            this.recording = val
            this.notifySubscribers()
        }
    },
    setBusy(val: boolean) {
        if (val !== this.busy) {
            this.busy = val
            this.notifySubscribers()
        }
    },
    notifySubscribers() {
        this.subscribers.forEach((callback) => callback())
    },
    reset() {
        this.recording = false
        this.busy = false
        this.subscribers.clear()
    },
}

export function resetCloudRecordingState() {
    state.reset()
}

export function useCloudRecording() {
    const { state: userState } = useContext(UserContext) as UserContextType
    const { state: roomState } = useContext(RoomContext) as RoomContextType
    const { startCloudRecording, upgradeToSfuRoom } = useRoomActions()

    const [hasCloudRecordingPerms, setHasCloudRecordingPerms] = useState(
        userState.profile?.policy?.canUseCloudRecording ?? false
    )
    const [cloudRecordingStarting, setCloudRecordingStarting] = useState(state.busy)
    const [isCloudRecording, setIsCloudRecording] = useState<boolean>(state.recording)

    useEffect(() => {
        if (!roomState.roomUrn) {
            state.setRecording(false)
            state.setBusy(false)
        }
    }, [roomState.roomUrn])

    useEffect(() => {
        setHasCloudRecordingPerms(userState.profile?.policy?.canUseCloudRecording ?? false)
    }, [userState.profile])

    useEffect(() => {
        const handleChange = () => {
            setCloudRecordingStarting(state.busy)
            setIsCloudRecording(state.recording)
        }
        state.subscribers.add(handleChange)
        return () => {
            state.subscribers.delete(handleChange)
        }
    }, [])

    useEffect(() => {
        if (roomState.sfuRoom) {
            state.setRecording(roomState.sfuRoom.isRecording)
            if (roomState.sfuRoom.isRecording) {
                state.setBusy(false)
            }
        }
        const h = (recording: boolean) => {
            state.setRecording(recording)
            if (recording) {
                state.setBusy(false)
            }
        }

        roomState.sfuRoom?.on('recordingStatusChanged', h)
        return () => {
            roomState.sfuRoom?.off('recordingStatusChanged', h)
        }
    }, [roomState.sfuRoom, roomState.sfuRoom?.isRecording])

    const handleStartCloudRecording = async () => {
        if (isCloudRecording || cloudRecordingStarting) {
            return
        }

        state.setBusy(true)
        if (roomState.expectedNetworkTopology !== 'sfu') {
            upgradeToSfuRoom()
            // Hopefully a SFU room will be created within 5 seconds.
            await sleep(5000)
        }

        if (!(await startCloudRecording())) {
            state.setBusy(false)
            return
        }

        // Avoid stuck loading state, allows user to retry
        setTimeout(() => {
            state.setBusy(false)
        }, 10_000)
    }

    return { isCloudRecording, handleStartCloudRecording, cloudRecordingStarting, hasCloudRecordingPerms }
}
